/* eslint-disable max-lines */
import { useAtomValue } from "jotai";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { article as articleData, section } from "shared/data";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { Share } from "scmp-app/components/article/article-2.0-action-widget/share";
import type { Props as ArticleLeadingProps } from "scmp-app/components/article/article-leading";
import { ImageSize } from "scmp-app/components/article/article-leading/article-leading-images/enums";
import type { ArticleRenderChildProps } from "scmp-app/components/article/article-render";
import { GlobalChecker } from "scmp-app/components/article/article-render/global-checker";
import { getArticleType } from "scmp-app/components/article/article-render/helpers";
import {
  useArticleBaseAdvertisingInfo,
  useMobileInlineAdSlotIsDisabledFlag,
  useReduceWhitespaceByAvailableSpace,
} from "scmp-app/components/article/article-render/hooks";
import { checkArticleIsInLiteUpgradeWall } from "scmp-app/components/article/article-render/hooks/piano-integration/helpers";
import { useContentHelper } from "scmp-app/components/content/hooks";
import type { InlineAdSlotRenderProps } from "scmp-app/components/schema-render/common/inline-ad-slot/hooks";
import { defineInlineAdSlotSchema } from "scmp-app/components/schema-render/common/inline-ad-slot/schemas";
import { SCMPLabLogo } from "scmp-app/components/scmp-lab-logo";
import { ExperimentTitleNames } from "scmp-app/lib/optimizely/consts";
import { useActiveExperiment } from "scmp-app/lib/optimizely/hooks";
import { rosettaAtom } from "scmp-app/lib/rosetta/atoms";
import type { genericArticleContent$key } from "scmp-app/queries/__generated__/genericArticleContent.graphql";

import {
  ActionWidgets,
  ActionWidgetsBar,
  Author,
  AuthorDetail,
  AuthorShortBio,
  BottomCarousel,
  Columnist,
  ColumnistWrapper,
  Content,
  ContentContainer,
  Correction,
  DesktopHeaderAdSlot,
  FurtherReadingInlineContainer,
  FurtherReadingRightSectionContainer,
  GenericArticleContainer,
  HeaderContainer,
  Headline,
  Leading,
  MobileContentHeaderAd,
  PaywallContainer,
  PublishDateTime,
  RelatedTopic,
  RightAvailableSpaceTracker,
  RightSectionContainer,
  RightSectionPlaceholder,
  SeriesCarousel,
  SeriesInline,
  Sponsor,
  StyledArticleContentRightSection,
  StyledArticleGoogleNewsButton,
  SubHeadline,
  TopicAndSection,
} from "./styles";

export type ArticleVariant = "generic" | "opinion" | "post-magazine" | "this-week-in-asia";

export type Props = ArticleRenderChildProps & {
  className?: string;
  extendedHeaderComponents?: React.ReactNode;
  leadingProps?: Partial<ArticleLeadingProps>;
  reference: genericArticleContent$key;
};

export const GenericArticle: FunctionComponent<Props> = ({
  className,
  extendedHeaderComponents,
  isInlinePaywallDomChanged,
  isShowInlinePaywall,
  leadingProps,
  provider,
  reference,
}) => {
  const article = useFragment(
    graphql`
      fragment genericArticleContent on Content {
        entityId
        flag
        sectionsV2 {
          value {
            ... on Section {
              entityId
            }
            ... on VideoSection {
              entityId
            }
          }
        }
        ...articleBaseAdvertisingInfoArticle
        ...globalCheckerContent
        ...helpersGetArticleTypeArticle
        ...helpersIsLiteWallArticle
        ...hooksContentHelperContent
        ...shareArticle20Content
      }
    `,
    reference,
  );

  const { zone } = useArticleBaseAdvertisingInfo(article);
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const isInlineAdSlotDisabled = useMobileInlineAdSlotIsDisabledFlag();
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const isSubscriber = useMemo(
    () => asyncRosettaState?.result?.user?.isSCMPSubscriber ?? false,
    [asyncRosettaState?.result?.user?.isSCMPSubscriber],
  );

  const withSticky = useMemo(() => {
    if (asyncRosettaState?.status === "success") {
      return (
        asyncRosettaState.result?.user === undefined ||
        asyncRosettaState.result?.user.isSCMPSubscriber === false
      );
    }
    return false;
  }, [asyncRosettaState]);

  const { isRecapArticleType } = useContentHelper(article);
  const articleType = getArticleType(article);

  const hasScmpLiteAccessOnly = useMemo(
    () =>
      (asyncRosettaState?.result?.hasScmpLiteAccessRight ?? false) &&
      !(asyncRosettaState?.result?.hasScmpDigitalAccessRight ?? false) &&
      !(asyncRosettaState?.result?.hasScmpPlusAccessRight ?? false),
    [
      asyncRosettaState?.result?.hasScmpLiteAccessRight,
      asyncRosettaState?.result?.hasScmpDigitalAccessRight,
      asyncRosettaState?.result?.hasScmpPlusAccessRight,
    ],
  );

  const appSettings = asyncRosettaState?.result?.appSettings ?? {};
  const { litePaywallTimeFrame = 3 } = appSettings;

  const isLiteUpgradePaywallActive = useMemo(
    () => checkArticleIsInLiteUpgradeWall(article, hasScmpLiteAccessOnly, litePaywallTimeFrame),
    [article, hasScmpLiteAccessOnly, litePaywallTimeFrame],
  );

  const isVideoArticle = useMemo(() => article.flag === articleData.flag.video, [article.flag]);

  const { reduceWhitespaceByAvailableSpaceSpec } = useReduceWhitespaceByAvailableSpace({
    firstAdSlotSize: 1000,
    isEnabled: isShowInlinePaywall !== undefined || notEmpty(isInlinePaywallDomChanged),
    specs: {
      adSlotSize: 600,
      whitespaceAllowance: 100,
      widgets: [
        {
          maxHeight: 470,
          name: "furtherReadingWidget",
        },
        {
          maxHeight: 560,
          name: "topicWidget",
          withAdSlot: !isSubscriber,
        },
        {
          maxHeight: isSubscriber ? 380 : 700,
          name: "beforeYouGoWidget",
          withAdSlot: false,
        },
      ],
    },
    trackers: {
      right: RightAvailableSpaceTracker as FunctionComponent,
    },
  });

  const firstSectionEntityIds = useMemo(() => {
    const firstSection = first(article?.sectionsV2)?.value;
    return firstSection?.map(section => section?.entityId ?? "") ?? [];
  }, [article.sectionsV2]);

  const articleVariant = useMemo<ArticleVariant>(() => {
    if (firstSectionEntityIds.includes(section.postMagazine.entityId)) {
      return "post-magazine";
    }
    if (firstSectionEntityIds.includes(section.thisWeekInAsia.entityId)) {
      return "this-week-in-asia";
    }

    if (
      articleType === articleData.types.column.entityId ||
      articleType === articleData.types.opinion.entityId
    ) {
      return "opinion";
    }

    return "generic";
  }, [firstSectionEntityIds, articleType]);

  const { experiment: articleAdDensityExperiment } = useActiveExperiment(
    ExperimentTitleNames.ArticleAdDensity,
  );
  const isArticleAdDensityVariantOne = useMemo(
    () => articleAdDensityExperiment?.variation?.name === "1",
    [articleAdDensityExperiment?.variation?.name],
  );
  const isMobileArticleInlineAdTestPlanEnabled = isArticleAdDensityVariantOne;

  const articleAdDensityTargeting = useMemo(() => {
    const variantName = articleAdDensityExperiment?.variation?.name;
    if (variantName === "0") return { targeting: { ABTest: "control" } as const };
    if (variantName === "1") return { targeting: { ABTest: "v1" } as const };
    return {};
  }, [articleAdDensityExperiment?.variation?.name]);

  const inlineAdSlots = useMemo<InlineAdSlotRenderProps>(() => {
    if (isDesktopUp) {
      return isSubscriber && !isLiteUpgradePaywallActive
        ? {
            "7": {
              adUnit: "outstream1",
            },
          }
        : {
            "1": {
              adUnit: "outstream1",
            },
            "7": {
              adUnit: "d_native5",
            },
          };
    }

    if (isMobileArticleInlineAdTestPlanEnabled) {
      /** mobile test plan ad position docs: https://docs.google.com/spreadsheets/d/11NRu19CbQ5gwbaOcHU-jpz9-CKHhRXipK0T8AIW24Mk/edit?gid=0#gid=0 */
      return {
        "0_adTest": {
          adUnit: "m_banner3",
          breakpoint: theme.breakpoints.down("desktop"),
          sizes: [
            [320, 100],
            [320, 50],
            [300, 100],
            [300, 50],
          ],
          ...articleAdDensityTargeting,
        },
        "1_adTest": {
          adUnit: "outstream1",
          breakpoint: theme.breakpoints.down("desktop"),
          setMinContainerHeight: true,
          ...articleAdDensityTargeting,
        },
        "2_adTest": {
          adUnit: "m_lrec1",
          breakpoint: theme.breakpoints.down("desktop"),
          sizes: [
            [300, 250],
            [320, 480],
          ],
          ...articleAdDensityTargeting,
        },
        "3_adTest": {
          adUnit: "m_banner1",
          breakpoint: theme.breakpoints.down("desktop"),
          isDisabled: isInlineAdSlotDisabled,
          sizes: [[300, 250]] as googletag.GeneralSize,
          withLabel: true,
          ...articleAdDensityTargeting,
        },
        "4_adTest": {
          adUnit: "m_native5",
          isDisabled: isInlineAdSlotDisabled,
          ...articleAdDensityTargeting,
        },
      };
    }

    return {
      "0": {
        adUnit: "outstream1",
        setMinContainerHeight: true,
        ...articleAdDensityTargeting,
      },
      "1": {
        adUnit: "m_banner1",
        breakpoint: theme.breakpoints.down("desktop"),
        sizes: [[300, 250]],
        ...articleAdDensityTargeting,
      },
      "2": {
        adUnit: "m_lrec1",
        breakpoint: theme.breakpoints.down("desktop"),
        sizes: [
          [300, 250],
          [320, 480],
        ],
        ...articleAdDensityTargeting,
      },
      "3": {
        adUnit: "m_lrec2",
        breakpoint: theme.breakpoints.down("desktop"),
        sizes: [[300, 250]],
        ...articleAdDensityTargeting,
      },
      "4": {
        adUnit: "m_lrec4d",
        breakpoint: theme.breakpoints.down("desktop"),
        isDisabled: isInlineAdSlotDisabled,
        sizes: [
          [300, 250],
          [320, 480],
        ],
        ...articleAdDensityTargeting,
      },
      "5": {
        adUnit: "m_lrec4e",
        breakpoint: theme.breakpoints.down("desktop"),
        isDisabled: isInlineAdSlotDisabled,
        sizes: [[300, 250]],
        ...articleAdDensityTargeting,
      },
      "6": {
        adUnit: "m_lrec4f",
        breakpoint: theme.breakpoints.down("desktop"),
        isDisabled: isInlineAdSlotDisabled,
        sizes: [[300, 250]],
        ...articleAdDensityTargeting,
      },
      "7": {
        adUnit: "m_native5",
        isDisabled: isInlineAdSlotDisabled,
        ...articleAdDensityTargeting,
      },
    };
  }, [
    articleAdDensityTargeting,
    isDesktopUp,
    isInlineAdSlotDisabled,
    isLiteUpgradePaywallActive,
    isMobileArticleInlineAdTestPlanEnabled,
    isSubscriber,
  ]);

  return (
    <GenericArticleContainer $articleVariant={articleVariant} className={className}>
      <DesktopHeaderAdSlot withSticky={withSticky}>
        {provider.adslot({
          adUnit: "d_banner1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [
            [970, 250],
            [728, 90],
            [970, 90],
          ],
        })}
      </DesktopHeaderAdSlot>
      <HeaderContainer>
        {extendedHeaderComponents}
        <BaseLinkContextProvider
          customQueryParameters={{
            module: "breadcrumb",
            pgtype: "article",
          }}
        >
          <TopicAndSection>
            {provider.topic()}
            {provider.sections()}
          </TopicAndSection>
        </BaseLinkContextProvider>
        {articleVariant === "opinion" && (
          <Columnist>
            {provider.authorAvatars({
              variant: "opinion",
              withoutLink: true,
            })}
            <ColumnistWrapper>
              {provider.headlineTag({ hideTooltip: true, variant: "generic" })}
              {provider.authorNames({ withLocation: false })}
            </ColumnistWrapper>
          </Columnist>
        )}
        <Headline $articleVariant={articleVariant}>
          {provider.headline({ hideTag: articleVariant === "opinion", variant: "generic" })}
        </Headline>
        <SubHeadline>{provider.subHeadline()}</SubHeadline>
        <Sponsor>
          {provider.sponsor({
            sponsorTagStyle: {
              layoutDirection: "horizontal",
            },
          })}
        </Sponsor>
        <ActionWidgets>
          <ActionWidgetsBar>
            {!isVideoArticle && provider.readingTime()}
            <Share position="article_header" reference={article} />
            {provider.actionWidget({ variant: "article-2.0" })}
            {provider.speechPlayer({
              variant: "article-2.0-minify",
            })}
            {provider.speechPlayerPortal({
              variant: "article-2.0-minify",
              withMinifyCleanUpButton: false,
            })}
          </ActionWidgetsBar>
          <StyledArticleGoogleNewsButton articleId={article.entityId} />
        </ActionWidgets>
      </HeaderContainer>
      {reduceWhitespaceByAvailableSpaceSpec.right.trackerNode}

      <StyledArticleContentRightSection
        {...reduceWhitespaceByAvailableSpaceSpec.right}
        entityId={article.entityId}
        provider={provider}
      >
        {shouldRenderWidgets => (
          <>
            {shouldRenderWidgets["furtherReadingWidget"] && (
              <FurtherReadingRightSectionContainer $stickyStackedHeight={appBarStickyHeight}>
                {provider.furtherReading({
                  inlineAds: provider.adslot({
                    adUnit: "d_native2b",
                    breakpoint: theme.breakpoints.up("desktop"),
                    sizes: ["fluid"],
                  }),
                  variant: "right-hand-side",
                })}
                {provider.adslot({
                  adUnit: "d_lrec2",
                  breakpoint: theme.breakpoints.up("desktop"),
                  sizes: [
                    [300, 250],
                    [300, 600],
                  ],
                  withSticky: true,
                })}
              </FurtherReadingRightSectionContainer>
            )}

            {shouldRenderWidgets["topicWidget"] && (
              <RightSectionContainer $stickyStackedHeight={appBarStickyHeight}>
                {provider.topicWidget({
                  inlineAds: provider.adslot({
                    adUnit: "d_native2d",
                    breakpoint: theme.breakpoints.up("desktop"),
                    sizes: ["fluid"],
                  }),
                })}
                {!isSubscriber &&
                  provider.adslot({
                    adUnit: "d_lrec3b",
                    autoRefreshOptions: {
                      scrollBackRefreshOptions: { slotArticleEntityId: article.entityId },
                    },
                    breakpoint: theme.breakpoints.up("desktop"),
                    sizes: [
                      [300, 250],
                      [300, 600],
                    ],
                    withSticky: true,
                  })}
              </RightSectionContainer>
            )}

            {shouldRenderWidgets["beforeYouGoWidget"] && (
              <>
                {/** placeholder to make the position of topic widget to be evenly distributed */}
                <RightSectionPlaceholder />
                <RightSectionContainer $stickyStackedHeight={appBarStickyHeight}>
                  {provider.beforeYouGoWidget(provider, {
                    children: provider.scmpPollWidget({
                      adSlotProps: {
                        breakpoint: theme.breakpoints.up("desktop"),
                        zone,
                      },
                    }),
                  })}
                </RightSectionContainer>
              </>
            )}
          </>
        )}
      </StyledArticleContentRightSection>
      <Leading>
        {provider.leading({
          responsiveDescriptionVariants: {
            desktopUp: "show",
            mobileUp: "show",
            tabletUp: "show",
          },
          responsiveImageVariants: { desktopUp: "normal", mobileUp: "normal", tabletUp: "normal" },
          responsiveVariants: {
            desktopUp: ImageSize["1200x800"],
            mobileUp: ImageSize["1020x680"],
            tabletUp: ImageSize["1020x680"],
          },
          toggleTitle: true,
          ...leadingProps,
        })}
      </Leading>
      <ContentContainer>
        <Author>
          {provider.authorNames({ withLocation: articleVariant !== "opinion" })}
          {isRecapArticleType && <SCMPLabLogo />}
        </Author>
        {articleVariant === "opinion" && (
          <AuthorShortBio>{provider.authorShortBio()}</AuthorShortBio>
        )}
        <PublishDateTime>{provider.publishedDate({ variant: "article-2.0" })}</PublishDateTime>

        <Correction>{provider.correction()}</Correction>
        <Content>
          {provider.body({
            extraComponentMap: {
              "inline-widget": null,
            },
            extraRenderFunctionMap: {
              "further-reading-widget": (_, keyIndex) => (
                <FurtherReadingInlineContainer key={keyIndex}>
                  {provider.furtherReading({
                    inlineAds: provider.adslot({
                      adUnit: "m_native2b",
                      breakpoint: theme.breakpoints.between("mobile", "desktop"),
                      isDisabled: isInlineAdSlotDisabled,
                      sizes: ["fluid"],
                    }),
                    variant: "inline",
                  })}
                </FurtherReadingInlineContainer>
              ),
            },
            inlineAdSlots,
            preprocessSchema: (schema: Schema) => {
              const newSchema = [...schema];
              const targetAdSlotTag = isSubscriber ? "5" : "4";
              const targetAdSlotTagIndex = newSchema.findIndex(
                node => node.type === "inline-ad-slot" && node.attribs?.tag === targetAdSlotTag,
              );

              const furtherReadingWidgetNodeIndex =
                targetAdSlotTagIndex === -1
                  ? newSchema.findLastIndex(node => node.type === "inline-ad-slot") + 1
                  : targetAdSlotTagIndex + 1;

              newSchema.splice(furtherReadingWidgetNodeIndex, 0, {
                type: "further-reading-widget",
              });

              if (!isMobileArticleInlineAdTestPlanEnabled) return newSchema;

              const getHasHardLinkChild = (node: SchemaNode) =>
                node.children?.[0]?.type === "a" && node.children.length === 1;
              const getIsTextParagraph = (node: SchemaNode) =>
                node.type === "p" && !getHasHardLinkChild(node);

              const findAdSlotPosition = (schema: Schema, position: number) => {
                let slotCount = 0;
                return schema.findIndex(node => {
                  if (getIsTextParagraph(node)) slotCount++;
                  return slotCount >= position;
                });
              };

              const inlineAdSlotSchemaList = [
                defineInlineAdSlotSchema({ row: 2, tag: "0_adTest" }),
                defineInlineAdSlotSchema({ row: 4, tag: "1_adTest" }),
                defineInlineAdSlotSchema({ row: 6, tag: "2_adTest" }),
                defineInlineAdSlotSchema({ row: 10, tag: "3_adTest" }),
                defineInlineAdSlotSchema({ row: -1, tag: "4_adTest" }),
              ];

              const newAdTestSchema = schema.filter(node => node.type !== "inline-ad-slot");
              inlineAdSlotSchemaList.forEach(inlineAdSlotSchema => {
                const position = findAdSlotPosition(newAdTestSchema, inlineAdSlotSchema.row);
                if (position > 0) newAdTestSchema.splice(position + 1, 0, inlineAdSlotSchema);
                if (inlineAdSlotSchema.row === -1)
                  newAdTestSchema.splice(-1, 0, inlineAdSlotSchema);
              });
              return newAdTestSchema;
            },
          })}
        </Content>

        <PaywallContainer>{provider.paywall()}</PaywallContainer>

        <AuthorDetail>
          {provider.authorDetails({
            variant: "generic",
          })}
        </AuthorDetail>

        <RelatedTopic>{provider.relatedTopics()}</RelatedTopic>

        <BottomCarousel>{provider.bottomCarouselWidget(provider)}</BottomCarousel>

        <SeriesInline>{provider.series({ variant: "inline" })}</SeriesInline>

        {!isMobileArticleInlineAdTestPlanEnabled && (
          <MobileContentHeaderAd>
            {provider.adslot({
              adUnit: "m_banner3",
              autoRefreshOptions: {
                scrollBackRefreshOptions: {
                  slotArticleEntityId: article.entityId,
                },
              },
              breakpoint: theme.breakpoints.down("desktop"),
              sizes: [
                [320, 100],
                [320, 50],
                [300, 100],
                [300, 50],
              ],
              withBackground: true,
              withLabel: true,
              ...articleAdDensityTargeting,
            })}
          </MobileContentHeaderAd>
        )}

        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <SeriesCarousel>{provider.series({ variant: "carousel" })}</SeriesCarousel>
          </GlobalChecker>
        </ClientSideSuspense>
      </ContentContainer>
    </GenericArticleContainer>
  );
};

GenericArticle.displayName = "GenericArticle";
